import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  ShieldCheck,
  Lock,
  Clock,
  Users,
  CheckCircle2,
  Layers,
  TrendingUp,
  Zap,
  Award,
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Compass,
  Building2,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { SERVICES } from "@/data/services";
import ContourBackground from "@/components/ContourBackground";
import StatesSlider from "@/components/StatesSlider";
import useSeo from "@/hooks/useSeo";

export default function Home() {
  useSeo({
    title: "Tri-Axis XYZ CAD Solutions — CAD Drafting for Land Surveyors",
    description:
      "Production-grade CAD drafting for land-surveying firms in the USA, Canada and Australia. ALTA/NSPS, boundary, topo, plats, ROW, GIS, 3D modeling.",
  });
  return (
    <div data-testid="page-home">
      <Hero />
      <TrustBar />
      <ClientsAchieve />
      <ServicesGrid />
      <EngagementModels />
      <WhyChooseUs />
      <Workflow />
      <Testimonials />
      <div id="home-coverage">
        <StatesSlider
          heading="Active in all 50 U.S. states, 13 Canadian provinces & 8 Australian regions."
          subheading="Browse every state, province, and territory our survey techies are currently producing drawings for."
        />
      </div>
      <FAQ />
    </div>
  );
}

/* ---------- 1. Hero (3-slide carousel) ---------- */
const HERO_SLIDES = [
  {
    id: "core",
    eyebrow: "CAD Drafting · Outsourced · Est. 2026",
    badgeIcon: <Award className="h-4 w-4 text-[var(--c-orange)]" />,
    title: (
      <>
        Outsourced <span className="text-[var(--c-topo)]">CAD drafting</span> for{" "}
        <span className="text-[var(--c-orange)]">land-surveying firms</span> in the
        USA, Canada & Australia.
      </>
    ),
    blurb:
      "Tri-Axis XYZ CAD Solutions becomes your behind-the-scenes drafting partner — ALTA, boundary, topo, plats, as-builts, ROW, GIS, and 3D modeling — delivered to your firm's standards, on your timeline, at half the production cost.",
    primary: { to: "/contact", label: "Request a Free Quote", testid: "hero-cta-quote" },
    secondary: { to: "/services", label: "Explore Services", testid: "hero-cta-services" },
    image: "https://static.prod-images.emergentagent.com/jobs/d8d95525-a408-489d-8aaa-625abe3f4e8c/images/870fdb4d462ccdcbbf416c19997742ab43459676a4f85bad821b0d8bc5ce8d45.png",
    imageLabel: { tl: "SHEET · A-001 · TRI-AXIS", br: "REV 02 · FEB 2026" },
    floatBadge: { eyebrow: "3-Tier QC", title: "Sealed Deliverables" },
    badges: [
      { icon: <ShieldCheck className="h-4 w-4" />, label: "NDA-Ready" },
      { icon: <Lock className="h-4 w-4" />, label: "ISO-Aligned QC" },
      { icon: <Clock className="h-4 w-4" />, label: "24-Hr Turnaround" },
      { icon: <Users className="h-4 w-4" />, label: "Survey-Licensed Reviewers" },
    ],
  },
  {
    id: "survey",
    eyebrow: "🧭 Survey Techies · Land Survey CAD",
    badgeIcon: <Compass className="h-4 w-4 text-[var(--c-topo)]" />,
    title: (
      <>
        Embed senior{" "}
        <span className="text-[var(--c-emerald)]">land-survey CAD techies</span>{" "}
        into your firm — by the hour, day, or full-time.
      </>
    ),
    blurb:
      "Survey-licensed reviewers, Civil 3D / MicroStation / Trimble Business Center / Carlson fluency, and 11 specialized service lines (ALTA, boundary, topo, plats, ROW, GIS). Trained on your standards before they touch a sheet.",
    primary: { to: "/survey-techies", label: "Hire a Survey Techie", testid: "hero-cta-survey-techies" },
    secondary: { to: "/services#survey-services", label: "Survey services (11)", testid: "hero-cta-survey-services" },
    image: "https://images.unsplash.com/photo-1581094271901-8022df4466f9?crop=entropy&cs=srgb&fm=jpg&q=85",
    imageLabel: { tl: "SLIDE · 02 · SURVEY", br: "11 SERVICES" },
    floatBadge: { eyebrow: "Licensed-led", title: "Survey-First QC" },
    badges: [
      { icon: <Compass className="h-4 w-4" />, label: "ALTA / NSPS" },
      { icon: <Layers className="h-4 w-4" />, label: "Boundary & Plats" },
      { icon: <TrendingUp className="h-4 w-4" />, label: "Topo · GIS · ROW" },
      { icon: <Award className="h-4 w-4" />, label: "PLS-Sealed Ready" },
    ],
  },
  {
    id: "cad",
    eyebrow: "📐 CAD Techies · Beyond Survey",
    badgeIcon: <Building2 className="h-4 w-4 text-[var(--c-orange)]" />,
    title: (
      <>
        Architectural, structural, MEP &amp;{" "}
        <span className="text-[var(--c-orange)]">mechanical CAD techies</span>, on tap.
      </>
    ),
    blurb:
      "12 non-survey CAD disciplines under one bench: architecture, structural detailing, MEP/BIM coordination, mechanical, electrical, plumbing/HVAC, civil engineering, 3D rendering, PDF-to-CAD, solar PV and signage.",
    primary: { to: "/cad-techies", label: "Hire a CAD Techie", testid: "hero-cta-cad-techies" },
    secondary: { to: "/services#cad-services", label: "CAD services (12)", testid: "hero-cta-cad-services" },
    image: "https://images.unsplash.com/photo-1581092335397-9583eb92d232?crop=entropy&cs=srgb&fm=jpg&q=85",
    imageLabel: { tl: "SLIDE · 03 · CAD", br: "12 SERVICES" },
    floatBadge: { eyebrow: "Revit · Tekla · SW", title: "BIM-Ready Models" },
    badges: [
      { icon: <Building2 className="h-4 w-4" />, label: "Architectural" },
      { icon: <Layers className="h-4 w-4" />, label: "Structural" },
      { icon: <Zap className="h-4 w-4" />, label: "MEP & BIM" },
      { icon: <CheckCircle2 className="h-4 w-4" />, label: "Mechanical" },
    ],
  },
];

const SLIDE_INTERVAL = 8000;

function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer.current);
    // `index` is intentionally not in deps — we use the functional setter so React
    // doesn't need it. Resetting the timer on every dot/arrow click would also
    // double-tick on rapid prev/next presses.
  }, [paused]);

  const goto = (i) => setIndex(((i % HERO_SLIDES.length) + HERO_SLIDES.length) % HERO_SLIDES.length);
  const slide = HERO_SLIDES[index];

  return (
    <section
      className="relative overflow-hidden border-b border-[var(--c-border)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-carousel"
    >
      <div className="absolute inset-0 tech-grid opacity-60" />
      <ContourBackground variant="light" opacity={0.55} seed={3} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            data-testid={`hero-slide-${slide.id}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
          >
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="tri-badge"
                data-testid="hero-eyebrow"
              >
                {slide.eyebrow}
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.1 }}
                className="display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[var(--c-ink)] leading-[1.02]"
              >
                {slide.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.18 }}
                className="mt-6 max-w-2xl text-base sm:text-lg text-[var(--c-muted)] leading-relaxed"
              >
                {slide.blurb}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.26 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Link
                  to={slide.primary.to}
                  data-testid={slide.primary.testid}
                  className="inline-flex items-center gap-2 rounded-sm bg-[var(--c-orange)] hover:bg-[var(--c-orange-dark)] text-white text-sm font-semibold px-5 py-3 transition-transform hover:-translate-y-0.5"
                >
                  {slide.primary.label} <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to={slide.secondary.to}
                  data-testid={slide.secondary.testid}
                  className="inline-flex items-center gap-2 rounded-sm border border-[var(--c-border)] hover:border-[var(--c-ink)] text-[var(--c-ink)] text-sm font-semibold px-5 py-3 bg-white transition-transform hover:-translate-y-0.5"
                >
                  {slide.secondary.label} <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.34 }}
                className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl"
              >
                {slide.badges.map((b) => (
                  <div
                    key={b.label}
                    data-testid={`hero-trust-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-2 border border-[var(--c-border)] bg-white/70 px-3 py-2 transition-colors hover:bg-white hover:border-[var(--c-ink)]"
                  >
                    <span className="text-[var(--c-topo)]">{b.icon}</span>
                    <span className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--c-ink)] font-semibold">
                      {b.label}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 22 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 0.18 }}
              className="lg:col-span-5"
            >
              <div className="relative corner-marks text-[var(--c-ink)] border border-[var(--c-border)] overflow-hidden bg-white">
                <img
                  src={slide.image}
                  alt={slide.eyebrow}
                  className="w-full h-[460px] object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
                <div className="absolute top-3 left-3 mono text-[10px] tracking-[0.22em] uppercase text-white/90 bg-black/40 px-2 py-0.5">
                  {slide.imageLabel.tl}
                </div>
                <div className="absolute bottom-3 right-3 mono text-[10px] tracking-[0.22em] uppercase text-white/90 bg-black/40 px-2 py-0.5">
                  {slide.imageLabel.br}
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-white/95 border border-[var(--c-border)] px-3 py-2">
                  {slide.badgeIcon}
                  <div>
                    <div className="mono text-[9px] tracking-[0.18em] uppercase text-[var(--c-muted)]">
                      {slide.floatBadge.eyebrow}
                    </div>
                    <div className="display text-xs font-extrabold text-[var(--c-ink)]">
                      {slide.floatBadge.title}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Slide controls */}
        <div className="mt-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2" data-testid="hero-dots">
            {HERO_SLIDES.map((s, i) => (
              <button
                type="button"
                key={s.id}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${s.id}`}
                onClick={() => goto(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index
                    ? "bg-[var(--c-orange)] w-10"
                    : "bg-slate-300 w-3 hover:bg-slate-400"
                }`}
              />
            ))}
            <span className="ml-3 mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
              {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")} · {slide.id}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="hero-prev"
              onClick={() => goto(index - 1)}
              aria-label="Previous slide"
              className="inline-flex h-9 w-9 items-center justify-center border border-slate-300 hover:border-slate-900 bg-white transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="hero-next"
              onClick={() => goto(index + 1)}
              aria-label="Next slide"
              className="inline-flex h-9 w-9 items-center justify-center border border-slate-300 hover:border-slate-900 bg-white transition-colors"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 2. Trust Bar (region flags + software stack) ---------- */
function TrustBar() {
  const regions = [
    { code: "us", flag: "🇺🇸", label: "USA — 50 States" },
    { code: "ca", flag: "🇨🇦", label: "Canada — 13 Provinces" },
    { code: "au", flag: "🇦🇺", label: "Australia — 8 Regions" },
  ];
  const tools = [
    "AutoCAD",
    "Civil 3D",
    "MicroStation",
    "Trimble Business Center",
    "Carlson",
    "ArcGIS",
    "Bentley OpenRoads",
    "Revit",
  ];
  return (
    <div className="border-b border-[var(--c-border)] bg-[var(--c-paper-dark)]/40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-4 flex flex-wrap items-center gap-4">
          <span className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-muted)]">
            Serving →
          </span>
          {regions.map((r) => (
            <div
              key={r.code}
              data-testid={`trustbar-region-${r.code}`}
              className="flex items-center gap-2"
            >
              <span className="text-lg">{r.flag}</span>
              <span className="mono text-xs font-semibold text-[var(--c-ink)]">
                {r.label}
              </span>
            </div>
          ))}
        </div>
        <div className="lg:col-span-8 flex items-center gap-6 overflow-x-auto">
          <span className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-muted)] whitespace-nowrap">
            Drafted in →
          </span>
          {tools.map((t) => (
            <span
              key={t}
              className="mono text-xs font-semibold text-[var(--c-ink)] whitespace-nowrap"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- 3. What Clients Achieve (4 stat cards) ---------- */
function ClientsAchieve() {
  const stats = [
    {
      k: "50%",
      v: "Lower drafting cost",
      d: "Versus hiring an in-house junior drafter — without sacrificing quality.",
      icon: <TrendingUp className="h-5 w-5" />,
    },
    {
      k: "24 hr",
      v: "Avg. first-draft turnaround",
      d: "Overnight production — drawings on your desk by morning.",
      icon: <Clock className="h-5 w-5" />,
    },
    {
      k: "98%",
      v: "On-time delivery rate",
      d: "Tracked across 5,000+ drawings delivered to 120+ firms.",
      icon: <CheckCircle2 className="h-5 w-5" />,
    },
    {
      k: "3-Tier",
      v: "Survey-licensed QC",
      d: "Drafter → senior reviewer → licensed surveyor final check.",
      icon: <ShieldCheck className="h-5 w-5" />,
    },
  ];
  return (
    <section id="home-outcomes" className="border-b border-[var(--c-border)]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div data-reveal="up" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
              01 · What clients achieve
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
              Measurable outcomes for{" "}
              <span className="text-[var(--c-topo)]">survey firms</span> who outsource
              with us.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[var(--c-muted)]">
            We don&apos;t replace your surveyors — we extend your drafting bench so your
            licensed staff can focus on field work and client relationships.
          </div>
        </div>

        <div data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--c-border)] border border-[var(--c-border)]">
          {stats.map((s, i) => (
            <div
              key={s.k}
              data-testid={`achieve-stat-${i}`}
              className="bg-white p-6 lg:p-7"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-[var(--c-topo)] text-white rounded-sm">
                {s.icon}
              </span>
              <div className="display mt-5 text-4xl font-extrabold tracking-tighter text-[var(--c-orange)]">
                {s.k}
              </div>
              <div className="display mt-2 text-base font-extrabold tracking-tight text-[var(--c-ink)]">
                {s.v}
              </div>
              <p className="mt-2 text-sm text-[var(--c-muted)] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 4. Services Grid (9 cards) ---------- */
function ServicesGrid() {
  // Pick top 9 services for homepage grid.
  const featured = SERVICES.slice(0, 9);
  return (
    <section id="home-services" className="border-b border-[var(--c-border)] bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div data-reveal="up" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
              02 · CAD Drafting Services
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
              The full <span className="text-[var(--c-orange)]">survey-drafting</span>{" "}
              stack.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[var(--c-muted)]">
            Eleven specialized service lines — each backed by a senior CAD lead, a QC
            reviewer, and a survey-licensed checker.
          </div>
        </div>

        <div data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--c-border)] border border-[var(--c-border)]">
          {featured.map((s, i) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`services-card-${s.slug}`}
              className="tri-card group bg-white p-6 lg:p-7 flex flex-col gap-4 min-h-[240px] relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl leading-none">{s.emoji}</span>
                <span className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-muted)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div>
                <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-[var(--c-ink)]">
                  {s.name}
                </div>
                <div className="mt-2 text-sm text-[var(--c-muted)] leading-relaxed">
                  {s.short}
                </div>
              </div>
              <div className="mt-auto flex items-center gap-1 text-sm font-semibold text-[var(--c-topo)]">
                Learn more{" "}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            to="/services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-2 rounded-sm border border-[var(--c-border)] hover:border-[var(--c-ink)] text-[var(--c-ink)] text-sm font-semibold px-5 py-3 bg-white"
          >
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. Engagement Models ---------- */
function EngagementModels() {
  const models = [
    {
      tag: "Model A",
      name: "Dedicated Drafter",
      price: "Monthly retainer",
      d: "A senior CAD drafter assigned exclusively to your firm — full-time, your standards, your project queue. Ideal for firms with steady drawing volume.",
      best: "Best for firms with 40+ drawings/month",
      bullets: [
        "Same drafter every project",
        "Direct Slack/Teams channel",
        "Adopts your CAD standards & blocks",
        "Predictable monthly billing",
      ],
    },
    {
      tag: "Model B",
      name: "Hourly Bench",
      price: "Time & materials",
      d: "Tap into our drafting bench as you need it — billed hourly, no minimums. Perfect for variable workloads and project spikes.",
      best: "Best for variable workload firms",
      bullets: [
        "No retainer required",
        "Multi-drafter pool",
        "Scale up during deadline crunches",
        "Detailed time logs per task",
      ],
      highlight: true,
    },
    {
      tag: "Model C",
      name: "Per-Project",
      price: "Fixed quote",
      d: "Send the scope, get a fixed price. We deliver the finished drawing set to your standards — no surprises, no overage.",
      best: "Best for one-off or specialty projects",
      bullets: [
        "Fixed price per project",
        "Defined scope & milestones",
        "Includes 2 revision rounds",
        "Survey-licensed final check",
      ],
    },
  ];
  return (
    <section id="home-engagement" className="border-b border-[var(--c-border)] bg-[var(--c-paper-dark)]/30">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div data-reveal="up" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
              03 · Engagement Models
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
              Three ways to <span className="text-[var(--c-topo)]">work with us</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[var(--c-muted)]">
            Pick the engagement model that fits your firm's drawing volume, cash flow,
            and team structure — switch models anytime as your needs evolve.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--c-border)] border border-[var(--c-border)]">
          {models.map((m, i) => (
            <div
              key={m.tag}
              data-testid={`engagement-${m.tag.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative p-7 lg:p-8 flex flex-col ${
                m.highlight
                  ? "bg-[var(--c-cyanotype)] text-white"
                  : "bg-white text-[var(--c-ink)]"
              }`}
            >
              {m.highlight && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1 bg-[var(--c-orange)] text-white mono text-[9px] tracking-[0.22em] uppercase px-2 py-1">
                  <Zap className="h-3 w-3" /> Most popular
                </div>
              )}
              <div
                className={`mono text-[10px] tracking-[0.22em] uppercase ${
                  m.highlight ? "text-[var(--c-cream)]/70" : "text-[var(--c-topo)]"
                }`}
              >
                {m.tag}
              </div>
              <div className="display mt-3 text-2xl lg:text-3xl font-extrabold tracking-tighter">
                {m.name}
              </div>
              <div
                className={`mono mt-1 text-xs ${
                  m.highlight ? "text-[var(--c-cream)]/70" : "text-[var(--c-muted)]"
                }`}
              >
                {m.price}
              </div>
              <p
                className={`mt-4 text-sm leading-relaxed ${
                  m.highlight ? "text-[var(--c-cream)]/85" : "text-[var(--c-muted)]"
                }`}
              >
                {m.d}
              </p>
              <ul className="mt-5 space-y-2">
                {m.bullets.map((b) => (
                  <li
                    key={b}
                    className={`flex items-start gap-2 text-sm ${
                      m.highlight ? "text-white" : "text-[var(--c-ink)]"
                    }`}
                  >
                    <CheckCircle2
                      className={`h-4 w-4 mt-0.5 shrink-0 ${
                        m.highlight ? "text-[var(--c-orange-light)]" : "text-[var(--c-emerald)]"
                      }`}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div
                className={`mt-6 pt-4 border-t mono text-[10px] tracking-[0.18em] uppercase ${
                  m.highlight
                    ? "border-white/15 text-[var(--c-cream)]/70"
                    : "border-[var(--c-border)] text-[var(--c-muted)]"
                }`}
              >
                {m.best}
              </div>
              <Link
                to="/contact"
                data-testid={`engagement-cta-${i}`}
                className={`mt-5 inline-flex items-center justify-center gap-2 rounded-sm text-sm font-semibold px-4 py-2.5 transition-colors ${
                  m.highlight
                    ? "bg-[var(--c-orange)] hover:bg-[var(--c-orange-dark)] text-white"
                    : "border border-[var(--c-border)] hover:border-[var(--c-ink)] text-[var(--c-ink)] bg-white"
                }`}
              >
                Discuss this model <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 6. Why Choose Us ---------- */
function WhyChooseUs() {
  const items = [
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      t: "Survey-licensed oversight",
      d: "Every deliverable reviewed by a licensed surveyor before it reaches your inbox — not just CAD drafters.",
    },
    {
      icon: <Layers className="h-5 w-5" />,
      t: "Your standards, always",
      d: "We adopt your CAD templates, blocks, layer standards, line types, and naming conventions on day one.",
    },
    {
      icon: <Clock className="h-5 w-5" />,
      t: "Time-zone advantage",
      d: "Overnight production from our drafting hub — drawings ready for your QC review by morning, every morning.",
    },
    {
      icon: <Lock className="h-5 w-5" />,
      t: "NDA-protected workflow",
      d: "Strict NDA, encrypted file transfer, role-based access. Your client data and CAD IP never leave our secure pipeline.",
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      t: "Transparent pricing",
      d: "Hourly, per-project, or dedicated-FTE billing. No hidden seat licenses, no surprise line items, no markup games.",
    },
    {
      icon: <Award className="h-5 w-5" />,
      t: "Sealed deliverables",
      d: "Final drawings sealed by a survey-licensed reviewer where state regulations allow — ready for stamping by your PLS.",
    },
  ];
  return (
    <section id="home-why" className="border-b border-[var(--c-border)]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
            04 · Why Tri-Axis
          </div>
          <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
            Built by surveyors.
            <br />
            <span className="text-[var(--c-orange)]">Trusted by surveyors.</span>
          </h2>
          <p className="mt-5 text-[var(--c-muted)] leading-relaxed">
            Tri-Axis XYZ isn't a generic drafting agency. We're a survey-first
            production studio — founded by licensed surveyors, staffed by senior CAD
            drafters, and operated as the invisible extension of your firm.
          </p>
          <div className="mt-8 corner-marks border border-[var(--c-border)] overflow-hidden">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d8d95525-a408-489d-8aaa-625abe3f4e8c/images/1a8d6a605cd1856762e49c9a38b576224fb697aa37bcfd4469ab7ddcdf132891.png"
              alt="Drafting tools"
              className="w-full h-[280px] object-cover"
            />
          </div>
        </div>
        <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--c-border)] border border-[var(--c-border)]">
          {items.map((i, idx) => (
            <li
              key={i.t}
              data-testid={`whyus-item-${idx}`}
              className="bg-white p-6 lg:p-7"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-[var(--c-topo)] text-white rounded-sm">
                {i.icon}
              </span>
              <div className="display mt-4 text-lg font-extrabold tracking-tight text-[var(--c-ink)]">
                {i.t}
              </div>
              <p className="mt-2 text-sm text-[var(--c-muted)] leading-relaxed">{i.d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- 7. Workflow / Process ---------- */
function Workflow() {
  const steps = [
    {
      n: "01",
      t: "Brief & Standards Intake",
      d: "Share your CAD templates, blocks, layer standards, sample drawings, and project brief. We onboard your standards inside 24 hours.",
    },
    {
      n: "02",
      t: "Senior Drafter Assigned",
      d: "A senior drafter + QC reviewer is assigned within 4 business hours. You get a direct Slack/Teams channel and a project tracker link.",
    },
    {
      n: "03",
      t: "Draft Production",
      d: "First draft delivered in 24–72 hours depending on scope. You review red-lines through our shared portal, comment, request revisions.",
    },
    {
      n: "04",
      t: "3-Tier QC + Delivery",
      d: "Internal QC → senior reviewer → survey-licensed final check. Sealed DWG + PDF deliverables land in your inbox, ready for your PLS stamp.",
    },
  ];
  return (
    <section id="home-workflow" className="border-b border-[var(--c-border)] bg-[var(--c-cyanotype)] text-white relative overflow-hidden">
      <div className="absolute inset-0 blueprint-grid opacity-50" />
      <ContourBackground variant="dark" opacity={0.55} seed={11} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div data-reveal="up" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-orange-light)]">
              05 · Workflow
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
              A four-step{" "}
              <span className="text-[var(--c-orange-light)]">production pipeline</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[var(--c-cream)]/75">
            We embed into your firm like an extension of your in-house team — without
            the hiring, training, software-license, or HR overhead.
          </div>
        </div>

        <ol data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {steps.map((s) => (
            <li
              key={s.n}
              data-testid={`workflow-step-${s.n}`}
              className="bg-[var(--c-cyanotype-deep)] p-6 lg:p-7 relative"
            >
              <div className="display text-5xl font-extrabold tracking-tighter text-[var(--c-orange-light)] opacity-30 absolute top-3 right-4">
                {s.n}
              </div>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-orange-light)] relative">
                Step {s.n}
              </div>
              <div className="display mt-3 text-lg font-extrabold tracking-tight relative">
                {s.t}
              </div>
              <p className="mt-2 text-sm text-[var(--c-cream)]/75 leading-relaxed relative">
                {s.d}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------- 8. Testimonials ---------- */
function Testimonials() {
  const items = [
    {
      q: "Their team became an invisible extension of our firm — same standards, same blocks, half the production cost. We have not hired another in-house drafter in 18 months.",
      n: "Principal Surveyor",
      r: "Florida-licensed survey firm",
    },
    {
      q: "ALTA turnaround used to be our bottleneck. Tri-Axis delivers a first-pass draft in 48 hours that's cleaner than what my junior drafter produces in a week.",
      n: "Director of Survey Operations",
      r: "Texas commercial survey group",
    },
    {
      q: "We tried two other outsourcing shops before Tri-Axis. The difference is the survey-licensed QC layer — they catch things my own staff misses.",
      n: "Managing Partner",
      r: "Ontario, Canada survey practice",
    },
  ];
  return (
    <section id="home-testimonials" className="border-b border-[var(--c-border)]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div data-reveal="up" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
              06 · Client voices
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
              What survey-firm{" "}
              <span className="text-[var(--c-orange)]">principals</span> tell us.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--c-border)] border border-[var(--c-border)]">
          {items.map((t, i) => (
            <figure
              key={t.n + i}
              data-testid={`testimonial-${i}`}
              className="bg-white p-7 lg:p-8 flex flex-col"
            >
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-orange)] mb-4">
                ★ ★ ★ ★ ★
              </div>
              <blockquote className="serif text-lg leading-relaxed text-[var(--c-ink)] flex-1">
                "{t.q}"
              </blockquote>
              <figcaption className="mt-6 pt-4 border-t border-[var(--c-border)]">
                <div className="display text-sm font-extrabold text-[var(--c-ink)]">
                  {t.n}
                </div>
                <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--c-muted)] mt-1">
                  {t.r}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 9. FAQ ---------- */
function FAQ() {
  const faqs = [
    {
      q: "How do you protect our CAD IP and client data?",
      a: "Every engagement starts with a signed mutual NDA. We use encrypted file transfer (SFTP / encrypted cloud), role-based access controls, and a no-retention policy — we delete your project files from our servers 60 days after final delivery unless you request archival.",
    },
    {
      q: "Do you use our CAD standards and templates?",
      a: "Yes — that's the whole point. During onboarding (typically 24–48 hours) we ingest your DWG template, layer standards, blocks, line types, plot styles, and sample drawings. Every deliverable matches your firm's drafting DNA so your clients never see a stylistic difference.",
    },
    {
      q: "Can you seal and stamp drawings?",
      a: "Our senior reviewers include licensed surveyors who can apply a sealed final-check signature where state regulations allow it. In most jurisdictions, however, the final PLS stamp is applied by your in-house licensed surveyor — we deliver QC-passed, stamp-ready drawings.",
    },
    {
      q: "What's the typical turnaround?",
      a: "First draft: 24–72 hours depending on scope. Standard ALTA: 5–10 business days. Complex topographic with LiDAR: 7–14 business days. We give you a milestone schedule at quote acceptance and stick to it 98% of the time.",
    },
    {
      q: "Which CAD platforms do you support?",
      a: "AutoCAD, Civil 3D, MicroStation, Bentley OpenRoads, Carlson Survey, Trimble Business Center, Revit, ArcGIS, and Land Desktop legacy projects. If you work in something else, ask — we probably support it.",
    },
    {
      q: "Can we start with a small pilot project?",
      a: "Absolutely — most new firms start with a single pilot drawing (paid, fixed-price) to test our fit with their standards before signing a retainer or hourly agreement. No obligation, no setup fees.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="home-faq" className="border-b border-[var(--c-border)]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-[var(--c-topo)]">
            07 · FAQ
          </div>
          <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[var(--c-ink)]">
            Questions <span className="text-[var(--c-orange)]">survey principals</span>{" "}
            ask before signing.
          </h2>
          <p className="mt-5 text-[var(--c-muted)] leading-relaxed">
            Don't see your question?{" "}
            <Link
              to="/contact"
              data-testid="faq-contact-link"
              className="text-[var(--c-topo)] underline underline-offset-2 hover:text-[var(--c-orange)]"
            >
              Ask us directly →
            </Link>
          </p>
        </div>
        <ul className="lg:col-span-8 border border-[var(--c-border)] bg-white divide-y divide-[var(--c-border)]">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <li key={f.q} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 lg:px-7 py-5 text-left hover:bg-[var(--c-paper)]/60 transition-colors"
                >
                  <span className="display text-base lg:text-lg font-extrabold tracking-tight text-[var(--c-ink)]">
                    {f.q}
                  </span>
                  <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center border border-[var(--c-border)] text-[var(--c-topo)]">
                    {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 lg:px-7 pb-6 -mt-1 text-sm text-[var(--c-muted)] leading-relaxed max-w-3xl">
                    {f.a}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ---------- 10. Final CTA (removed — global PreFooterCTA used instead) ---------- */
